type FileSystemHandleLike = {
  kind?: string;
  getFile?: () => Promise<File>;
};

type FileSystemEntryLike = {
  isFile?: boolean;
  file?: (
    resolve: (file: File) => void,
    reject?: () => void,
  ) => void;
};

type TransferItem = {
  kind?: string;
  type?: string;
  getAsFile?: () => File | null;
  getAsFileSystemHandle?: () => Promise<FileSystemHandleLike | null>;
  webkitGetAsEntry?: () => FileSystemEntryLike | null;
};

type FileTransfer = {
  files?: ArrayLike<File> | null;
  items?: ArrayLike<TransferItem> | null;
};

const filesFromTransfer = (transfer?: FileTransfer | null): File[] =>
  Array.from(transfer?.files || []);

export const clipboardImageFiles = (transfer?: FileTransfer | null): File[] => {
  const directFiles = filesFromTransfer(transfer).filter((file) => file.type.startsWith('image/'));
  if (directFiles.length > 0) return directFiles;

  return Array.from(transfer?.items || [])
    .filter((item) => item.kind === 'file' && (item.type || '').startsWith('image/'))
    .map((item) => item.getAsFile?.() || null)
    .filter((file): file is File => file !== null);
};

const fileFromLegacyEntry = (entry?: FileSystemEntryLike | null): Promise<File | null> =>
  new Promise((resolve) => {
    if (!entry?.isFile || !entry.file) {
      resolve(null);
      return;
    }

    try {
      entry.file(resolve, () => resolve(null));
    } catch {
      resolve(null);
    }
  });

const fileFromItem = async (item: TransferItem): Promise<File | null> => {
  const directFile = item.getAsFile?.() || null;
  if (directFile) return directFile;

  if (item.getAsFileSystemHandle) {
    try {
      const handle = await item.getAsFileSystemHandle();
      if (handle?.kind === 'file' && handle.getFile) return await handle.getFile();
    } catch {}
  }

  if (item.webkitGetAsEntry) {
    try {
      return await fileFromLegacyEntry(item.webkitGetAsEntry());
    } catch {}
  }

  return null;
};

export const dragFiles = async (transfer?: FileTransfer | null): Promise<File[]> => {
  const directFiles = filesFromTransfer(transfer);
  if (directFiles.length > 0) return directFiles;

  const itemFiles = (await Promise.all(
    Array.from(transfer?.items || [])
      .filter((item) => item.kind === 'file')
      .map(fileFromItem),
  )).filter((file): file is File => file !== null);
  if (itemFiles.length > 0) return itemFiles;

  // Chromium/Brave can expose an empty FileList on the first read while the
  // native drop is still being promoted to the page. Retry once before
  // treating the event as fileless.
  return filesFromTransfer(transfer);
};
